<template>
    <details-drawer ref="drawer" :title="drawerTitle" :deleting="deleting"
                    :delete-disabled="kind === 'expense' && (loading || failed)"
                    @edit="editRecord" @delete="deleteRecord" @closed="$emit('closed')">
        <section class="details-drawer-identity" :class="{'details-item-identity': kind === 'item'}">
            <h2>{{recordTitle}}</h2>
            <div v-if="kind !== 'item'" class="details-drawer-balance">
                <span>{{$t('common.total')}}</span>
                <span>{{money(record.total)}}</span>
            </div>
        </section>
        <section class="details-drawer-section" v-loading="loading">
            <div v-if="failed" class="details-drawer-error" role="alert">
                <span>{{$t('recordDetails.loadFailed')}}</span>
                <button type="button" @click="loadDetails">{{$t('client.retry')}}</button>
            </div>
            <template v-else-if="details">
                <h3>{{$t('recordDetails.information')}}</h3>
                <dl v-if="kind === 'item'" class="details-drawer-fields">
                    <div v-if="details.itemCode"><dt>{{setting.codeLabel}}</dt><dd>{{details.itemCode}}</dd></div>
                    <div v-if="details.unit"><dt>{{$t('itemCreate.labelUnit')}}</dt><dd>{{details.unit}}</dd></div>
                    <div><dt>{{$t('common.rate')}}</dt><dd>{{money(details.rate)}}</dd></div>
                    <div v-if="details.isShowCost === 1"><dt>{{$t('itemCreate.labelCost')}}</dt><dd>{{money(details.cost)}}</dd></div>
                    <div v-if="details.isShowInventory === 1 && !inventoryLoading && !inventoryError"><dt>{{$t('itemCreate.currentInventory')}}</dt><dd>{{inventoryTotal}}</dd></div>
                </dl>
                <dl v-else-if="kind === 'expense'" class="details-drawer-fields">
                    <div><dt>{{$t('common.date')}}</dt><dd>{{formatDate(details.expenseDate * 1000)}}</dd></div>
                    <div v-if="details.merchant"><dt>{{$t('createExpense.merchant')}}</dt><dd>{{details.merchant}}</dd></div>
                    <div v-if="expenseCategory"><dt>{{$t('common.category')}}</dt><dd>{{expenseCategory}}</dd></div>
                    <div><dt>{{$t('common.tax')}}</dt><dd>{{money(details.tax)}}</dd></div>
                    <div><dt>{{$t('expense.tip')}}</dt><dd>{{money(details.tip)}}</dd></div>
                </dl>
                <dl v-else-if="kind === 'time'" class="details-drawer-fields">
                    <div v-if="details.clientName"><dt>{{$t('common.client')}}</dt><dd>{{details.clientName}}</dd></div>
                    <div><dt>{{$t('time.start')}}</dt><dd class="details-drawer-multiline">{{formatDateTime(details.startDate * 1000)}}</dd></div>
                    <div><dt>{{$t('time.end')}}</dt><dd class="details-drawer-multiline">{{formatDateTime(details.endDate * 1000)}}</dd></div>
                    <div><dt>{{$t('time.duration')}}</dt><dd>{{formatDuration(details.totalMinutes)}}</dd></div>
                    <div><dt>{{$t('common.rate')}}</dt><dd>{{money(details.rate)}}</dd></div>
                    <div v-if="details.rateName"><dt>{{$t('time.rateName')}}</dt><dd>{{details.rateName}}</dd></div>
                    <div><dt>{{$t('common.status')}}</dt><dd>{{$t(details.billed === 1 ? 'time.billed' : 'time.unbilled')}}</dd></div>
                    <div v-if="details.timeDescription"><dt>{{$t('common.description')}}</dt><dd class="details-drawer-multiline">{{details.timeDescription}}</dd></div>
                </dl>
            </template>
        </section>

        <section v-if="kind === 'expense' && receiptUrl !== ''" class="details-drawer-section">
            <h3>{{$t('recordDetails.receipt')}}</h3>
            <el-image class="details-drawer-receipt" :src="receiptUrl" :preview-src-list="[receiptUrl]" fit="contain">
                <div slot="error" class="details-drawer-error">{{$t('common.loadFailed')}}</div>
            </el-image>
        </section>

        <section v-if="kind === 'item'" class="details-drawer-section">
            <div class="details-history-heading details-inventory-heading">
                <h3>{{$t('recordDetails.inventoryHistory')}}</h3>
                <template v-if="details && !loading && !failed">
                    <el-button v-if="details.isShowInventory !== 1" class="details-inventory-action" :loading="enablingInventory"
                               @click="enableInventory">{{$t('recordDetails.enableInventory')}}</el-button>
                    <el-button v-else class="details-inventory-action" icon="el-icon-plus" :disabled="inventoryLoading || inventoryError"
                               @click="openInventoryDialog">{{$t('itemCreate.adjustInventory')}}</el-button>
                </template>
            </div>
            <div v-loading="inventoryLoading" class="details-history-content">
                <div v-if="inventoryError" class="details-drawer-error" role="alert">
                    <span>{{$t('recordDetails.inventoryLoadFailed')}}</span>
                    <button type="button" @click="loadInventory">{{$t('client.retry')}}</button>
                </div>
                <div v-else-if="!inventoryLoading && inventory.length === 0" class="details-history-empty">{{$t(details && details.isShowInventory !== 1 ? 'recordDetails.inventoryDisabled' : 'recordDetails.noInventory')}}</div>
                <div v-for="entry in inventory" :key="entry.innerUUID" class="details-inventory-row">
                    <span class="details-inventory-date">{{formatDateTime(entry.inventoryDate * 1000)}}</span>
                    <div class="details-inventory-note">
                        <span class="details-drawer-multiline">{{entry.note}}</span>
                        <button v-if="entry.invoiceUUID" class="details-inventory-invoice" type="button"
                                :disabled="openingInvoice" @click="openInvoice(entry.invoiceUUID)">{{entry.invoiceNum}} {{entry.clientName}}</button>
                    </div>
                    <span class="details-inventory-quantity" :class="{'is-increase': entry.inventoryNumber > 0}">{{entry.inventoryNumber > 0 ? '+' : ''}}{{entry.inventoryNumber}}</span>
                </div>
            </div>
        </section>

        <el-dialog v-if="kind === 'item'" :visible.sync="inventoryDialogVisible" :title="$t('itemCreate.adjustInventory')"
                   custom-class="details-inventory-dialog" width="480px" append-to-body :close-on-click-modal="false"
                   :close-on-press-escape="!inventorySaving" :show-close="!inventorySaving">
            <div class="details-inventory-summary">
                <span>{{$t('itemCreate.currentInventory')}}</span><span>{{inventoryTotal}}</span>
            </div>
            <el-form label-position="top" @submit.native.prevent="saveInventory">
                <el-form-item :label="$t('common.type')">
                    <el-radio-group v-model="inventoryDirection" class="details-inventory-direction">
                        <el-radio-button :label="1">+ {{$t('itemCreate.increase')}}</el-radio-button>
                        <el-radio-button :label="-1">− {{$t('itemCreate.decrease')}}</el-radio-button>
                    </el-radio-group>
                </el-form-item>
                <el-form-item :label="$t('itemCreate.columnQuantity')">
                    <el-input v-model="inventoryForm.quantity" inputmode="decimal" :placeholder="$t('itemCreate.placeholderQuantity')"></el-input>
                </el-form-item>
                <el-form-item :label="$t('itemCreate.placeholderDateAndTime')">
                    <el-date-picker v-model="inventoryForm.date" type="datetime" format="yyyy-MM-dd HH:mm"
                                    :placeholder="$t('itemCreate.placeholderDateAndTime')"></el-date-picker>
                </el-form-item>
                <el-form-item :label="$t('itemCreate.columnNote')">
                    <el-input v-model="inventoryForm.note" type="textarea" :autosize="{minRows: 2}" :placeholder="$t('itemCreate.placeholderNote')"></el-input>
                </el-form-item>
            </el-form>
            <div slot="footer">
                <el-button :disabled="inventorySaving" @click="inventoryDialogVisible = false">{{$t('common.cancel')}}</el-button>
                <el-button type="primary" :loading="inventorySaving" @click="saveInventory">{{$t('common.save')}}</el-button>
            </div>
        </el-dialog>
    </details-drawer>
</template>

<script>
    import DetailsDrawer from '@/components/common/DetailsDrawer'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {formatEnglishDate, formatLocalizedDateTime} from '@/utils/time'
    import {getStoredImageDownloadUrl} from '@/utils/fileUpload'
    import {timest, uuid, toTmpTime} from '@/utils/validate'
    import {isValidItemDecimal, setItemDecimalSign} from '@/utils/itemInputValidation'

    const DETAIL_ENDPOINTS = {
        item: 'query_item',
        expense: 'query_expense_for_web',
        time: 'query_time_tracker_user_web'
    };
    const TITLE_KEYS = {
        item: 'recordDetails.itemTitle',
        expense: 'recordDetails.expenseTitle',
        time: 'recordDetails.timeTitle'
    };
    const EDIT_PATHS = {item: '/itemUpdate', expense: '/updateExpense', time: '/timeUpdate'};

    export default {
        name: 'RecordDetailsDrawer',
        components: {DetailsDrawer},
        props: {
            kind: {type: String, required: true},
            record: {type: Object, required: true},
            companyUuid: {type: String, required: true},
            setting: {type: Object, required: true}
        },
        data() {
            return {
                details: null,
                loading: true,
                failed: false,
                deleting: false,
                categories: [],
                receiptImage: null,
                inventory: [],
                inventoryLoading: false,
                inventoryError: false,
                enablingInventory: false,
                inventoryDialogVisible: false,
                inventorySaving: false,
                inventoryDirection: 1,
                inventoryForm: {quantity: '', date: null, note: ''},
                openingInvoice: false
            };
        },
        computed: {
            drawerTitle() {
                return this.$t(TITLE_KEYS[this.kind]);
            },
            recordTitle() {
                if (this.kind === 'item') return this.record.itemDescription;
                if (this.kind === 'expense') return this.record.expenseDescription;
                return this.record.clientName;
            },
            inventoryTotal() {
                return this.inventory.reduce((total, entry) => this.$NP.plus(total, entry.inventoryNumber), 0);
            },
            receiptUrl() {
                return this.receiptImage === null ? '' : getStoredImageDownloadUrl(this.$store.state.requestURL, this.receiptImage);
            },
            expenseCategory() {
                const category = this.categories.find(entry => entry.innerUUID === this.details.categoryUUID);
                return category == null ? '' : category.name;
            }
        },
        methods: {
            money(value) {
                return xiaoshudianfenge(value, this.setting);
            },
            formatDate: formatEnglishDate,
            formatDateTime(timestamp) {
                return formatLocalizedDateTime(timestamp, this.$i18n.locale);
            },
            formatDuration(minutes) {
                return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
            },
            async loadDetails() {
                this.loading = true;
                this.failed = false;
                try {
                    const response = await this.$axios.post(DETAIL_ENDPOINTS[this.kind], {
                        params: {innerUUID: this.record.innerUUID, companyUUID: this.companyUuid}
                    });
                    if (response.data.errorCode !== 0) throw new Error('Record query failed');
                    this.details = response.data.objectData;
                    if (this.kind === 'expense') {
                        this.categories = response.data.categoryForWebList;
                        this.receiptImage = response.data.expenseImageForWeb == null ? null : response.data.expenseImageForWeb;
                    }
                } catch {
                    this.failed = true;
                } finally {
                    this.loading = false;
                }
            },
            async loadInventory() {
                this.inventoryLoading = true;
                this.inventoryError = false;
                try {
                    const response = await this.$axios.post('query_item_inventory_list', {
                        params: {
                            belongItemUUID: this.record.innerUUID,
                            companyUUID: this.companyUuid,
                            syncSuccessDate: 0,
                            appVersion: 700,
                            orderTpe: 1
                        }
                    });
                    if (response.data.errorCode !== 0) throw new Error('Inventory query failed');
                    this.inventory = response.data.dataList.filter(entry => Number(entry.syncStatus) !== 1);
                } catch {
                    this.inventoryError = true;
                } finally {
                    this.inventoryLoading = false;
                }
            },
            async enableInventory() {
                this.enablingInventory = true;
                try {
                    const response = await this.$axios.post('update_item', {params: {
                        innerUUID: this.record.innerUUID, companyUUID: this.companyUuid,
                        isShowInventory: 1, fromClient: 3, accessDate: timest()
                    }});
                    if (response.data.errorCode !== 0) throw new Error('Enable inventory failed');
                    this.details = response.data.objectData;
                    this.$message.success(this.$t('recordDetails.inventoryEnabled'));
                    this.$emit('updated');
                } catch {
                    this.$message.error(this.$t('common.saveFailed'));
                } finally {
                    this.enablingInventory = false;
                }
            },
            openInventoryDialog() {
                this.inventoryDirection = 1;
                this.inventoryForm = {quantity: '', date: new Date(), note: ''};
                this.inventoryDialogVisible = true;
            },
            async saveInventory() {
                if (this.inventorySaving) return;
                const quantity = this.inventoryForm.quantity.trim();
                if (quantity === '') {
                    this.$message.error(this.$t('itemCreate.msgQuantityRequired'));
                    return;
                }
                if (!isValidItemDecimal(quantity, false, 5)) {
                    this.$message.error(this.$t('itemCreate.msgInvalidInventory'));
                    return;
                }
                if (this.inventoryForm.date === null) {
                    this.$message.error(this.$t('itemCreate.msgDateRequired'));
                    return;
                }
                this.inventorySaving = true;
                try {
                    const response = await this.$axios.post('create_item_inventory', {params: {
                        innerUUID: uuid(null, 37), companyUUID: this.companyUuid, syncStatus: 0,
                        inventoryNumber: setItemDecimalSign(quantity, this.inventoryDirection),
                        inventoryDate: toTmpTime(this.inventoryForm.date), note: this.inventoryForm.note.trim(),
                        relatedItemUUID: this.record.innerUUID, belongItemUUID: this.record.innerUUID,
                        fromClient: 3, accessDate: timest(), createDate: timest()
                    }});
                    if (response.data.errorCode !== 0) throw new Error('Inventory adjustment failed');
                    this.inventoryDialogVisible = false;
                    this.$message.success(this.$t('recordDetails.inventorySaved'));
                    await this.loadInventory();
                    this.$emit('updated');
                } catch {
                    this.$message.error(this.$t('common.saveFailed'));
                } finally {
                    this.inventorySaving = false;
                }
            },
            editRecord() {
                const query = {innerUUID: this.record.innerUUID};
                this.$router.push({path: EDIT_PATHS[this.kind], query});
            },
            async openInvoice(innerUUID) {
                this.openingInvoice = true;
                try {
                    const response = await this.$axios.post('web_query_invoice', {params: {innerUUID, companyUUID: this.companyUuid}});
                    if (response.data.errorCode !== 0) throw new Error('Invoice query failed');
                    const invoice = response.data.invoice;
                    const route = Number(invoice.type) === 0
                        ? {path: '/invoiceUpdate', query: {innerUUID}}
                        : {path: '/updateEstimate', query: {innerUUID, type: invoice.type}};
                    this.$router.push(route);
                } catch {
                    this.$message.error(this.$t('client.invoicesLoadFailed'));
                } finally {
                    this.openingInvoice = false;
                }
            },
            async deleteRecord() {
                try {
                    await this.$confirm(this.$t('recordDetails.deleteConfirm'), this.$t('common.delete'), {
                        confirmButtonText: this.$t('common.delete'), cancelButtonText: this.$t('common.cancel'), type: 'warning'
                    });
                } catch {
                    return;
                }
                this.deleting = true;
                try {
                    let response;
                    if (this.kind === 'item') {
                        const params = {
                            entityType: 'ITEM', action: 'DELETE',
                            companyUUID: this.companyUuid, innerUUIDList: [this.record.innerUUID]
                        };
                        response = await this.$axios.post('web_batch_action', {params});
                    } else if (this.kind === 'time') {
                        response = await this.$axios.post('update_time_tracker', {params: {
                            innerUUID: this.record.innerUUID, companyUUID: this.companyUuid,
                            fromClient: 3, accessDate: timest(), syncStatus: 1
                        }});
                    } else if (this.kind === 'expense') {
                        const expense = Object.assign({}, this.details, {syncStatus: 1, accessDate: timest(), fromClient: 3, expenseImageUUIDArrayKey: ''});
                        const companyLogo = this.receiptImage === null ? null
                            : Object.assign({}, this.receiptImage, {syncStatus: 1, accessDate: timest()});
                        response = await this.$axios.post('web_create_expense', {params: {expense, companyLogo, categoryList: []}});
                    }
                    if (response.data.errorCode !== 0) throw new Error('Record deletion failed');
                    this.$message.success(this.$t('recordDetails.deleted'));
                    this.$refs.drawer.close();
                    this.$emit('deleted');
                } catch {
                    this.$message.error(this.$t('common.deleteFailed'));
                } finally {
                    this.deleting = false;
                }
            }
        },
        mounted() {
            this.loadDetails();
            if (this.kind === 'item') this.loadInventory();
        }
    }
</script>
